import React, { useState, useEffect } from "react";
import "./ProfileEdit.css";
import { useUser } from "@/contexts";
import { useNavigate } from "react-router-dom";
import {
  UserRecord,
  ContactRecord,
  contactsService,
  statesOfBrazilService,
  IBGECity,
} from "@/services";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "@/services/config";
import {
  optionsBrazilianStates,
  optionsBodyTypes,
  optionsSkinTones,
  optionsHairColors,
  optionsEyeColors,
  optionsHeights,
  optionsSmoking,
  optionsDrinking,
  optionsMaritalStatus,
  optionsChildren,
  optionsEducation,
  optionsProfessions,
} from "@/utils";

const ProfileEditScreen: React.FC = () => {
  const { userData, refreshUser, user } = useUser();
  const navigate = useNavigate();

  const [touched, setTouched] = useState<Record<string, boolean>>({
    country: false,
    state: false,
    city: false,
  });

  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = [
    "Janeiro",
    "Fevereiro",
    "Março",
    "Abril",
    "Maio",
    "Junho",
    "Julho",
    "Agosto",
    "Setembro",
    "Outubro",
    "Novembro",
    "Dezembro",
  ];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);

  const [formData, setFormData] = useState<UserRecord>({});

  const [step, setStep] = useState(1);

  const [cities, setCities] = useState<IBGECity[]>([]);

  const [isLoading, setIsLoading] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const CONTACT_OPTIONS = [
    "WhatsApp",
    "Telegram",
    "Instagram",
    "Facebook",
    "Privacy",
    "OnlyFans",
  ] as const;

  type ContactName = (typeof CONTACT_OPTIONS)[number];

  const [contacts, setContacts] = useState<ContactRecord[]>([]);

  const [selectedContactType, setSelectedContactType] =
    useState<ContactName>("WhatsApp");
  const [contactValues, setContactValues] = useState<
    Record<ContactName, string>
  >(() =>
    CONTACT_OPTIONS.reduce(
      (acc, name) => {
        acc[name] = "";
        return acc;
      },
      {} as Record<ContactName, string>,
    ),
  );

  const errors = React.useMemo(() => {
    const country = !formData.country ? "Selecione o país." : null;
    const state = !formData.state ? "Selecione o estado." : null;
    const city = !formData.city ? "Selecione a cidade." : null;

    return {
      country,
      state,
      city,
    };
  }, [formData]);

  const formatWhatsAppBR = (value: string) => {
    const digits = String(value || "").replace(/\D+/g, "");

    // remove 55 se já veio com DDI
    let rest = digits.startsWith("55") ? digits.slice(2) : digits;

    // limite: DDD(2) + número(9)
    rest = rest.slice(0, 11);

    const ddd = rest.slice(0, 2);
    const phone = rest.slice(2);

    if (rest.length === 0) return "+55 ";
    if (rest.length < 2) return `+55 (${ddd}`;
    if (rest.length === 2) return `+55 (${ddd}) `;

    if (phone.length <= 4) return `+55 (${ddd}) ${phone}`;
    if (phone.length <= 8)
      return `+55 (${ddd}) ${phone.slice(0, 4)}-${phone.slice(4)}`;
    return `+55 (${ddd}) ${phone.slice(0, 5)}-${phone.slice(5, 9)}`;
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name } = e.target;
    // @ts-ignore
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;

    if (name === "state") {
      setFormData((prev) => ({
        ...prev,
        state: value,
        city: "",
        cityId: undefined,
        region: undefined,
      }));

      return;
    }

    if (name === "city") {
      const selectedCity = cities.find((c) => c.nome === value);

      setFormData((prev) => ({
        ...prev,
        city: value,
        cityId: selectedCity?.id,
        region: selectedCity?.microrregiao.mesorregiao.UF.regiao.nome,
      }));

      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setTouched((prev) => ({
      ...prev,
      country: true,
      state: true,
      city: true,
    }));

    if (!formData.country || !formData.state || !formData.city) {
      return;
    }

    try {
      setIsLoading(true);

      const userRef = doc(db, "users", user.uid);
      const payload: any = {
        ...formData,
        updatedAt: serverTimestamp(),
      };

      // Limpeza de campos undefined
      Object.keys(payload).forEach(
        (key) => payload[key] === undefined && delete payload[key],
      );

      // 3. Salva TUDO de uma vez no Firestore
      await updateDoc(userRef, payload);

      // 4. Salva contatos em uma coleção separada
      const nextContacts: ContactRecord[] = CONTACT_OPTIONS.map((name) => {
        const raw = contactValues[name];
        const value =
          name === "WhatsApp"
            ? formatWhatsAppBR(raw || "+55 ")
            : String(raw || "").trim();
        return { name, value };
      }).filter((c) => c.value.length > 0);

      await contactsService.saveContacts(user.uid, nextContacts);

      setContacts(nextContacts);

      setContactValues((prev) => {
        const next = { ...prev };
        CONTACT_OPTIONS.forEach((name) => {
          next[name] = "";
        });
        nextContacts.forEach((c) => {
          if (CONTACT_OPTIONS.includes(c.name as ContactName)) {
            const name = c.name as ContactName;
            next[name] =
              name === "WhatsApp"
                ? formatWhatsAppBR(c.value || "+55 ")
                : String(c.value || "");
          }
        });
        return next;
      });

      await refreshUser();
      navigate("/meu-perfil");
    } catch (error) {
      console.error("Erro crítico ao salvar:", error);
      setErrorMessage(
        "Falha ao salvar foto ou dados. Verifique as permissões.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadCities = async () => {
      if (!formData.state) {
        setCities([]);
        return;
      }

      try {
        const result = await statesOfBrazilService.getCitiesByState(
          formData.state,
        );
        setCities(result);
      } catch (error) {
        console.error("Erro ao carregar cidades:", error);
        setCities([]);
      }
    };

    loadCities();
  }, [formData.state]);

  useEffect(() => {
    if (!userData) return;

    setFormData(userData);

    if (userData.state) {
      statesOfBrazilService
        .getCitiesByState(userData.state)
        .then(setCities)
        .catch(() => setCities([]));
    }
  }, [userData]);

  useEffect(() => {
    const loadContacts = async () => {
      if (!user?.uid) return;
      try {
        const list = await contactsService.getContacts(user.uid);
        setContacts(list);

        setContactValues(() => {
          const base = CONTACT_OPTIONS.reduce(
            (acc, name) => {
              acc[name] = "";
              return acc;
            },
            {} as Record<ContactName, string>,
          );

          list.forEach((c) => {
            if (CONTACT_OPTIONS.includes(c.name as ContactName)) {
              const name = c.name as ContactName;
              base[name] =
                name === "WhatsApp"
                  ? formatWhatsAppBR(c.value || "+55 ")
                  : String(c.value || "");
            }
          });

          return base;
        });

        // Default: WhatsApp. Se não existir opção, cai na primeira.
        setSelectedContactType((prev) =>
          CONTACT_OPTIONS.includes(prev) ? prev : CONTACT_OPTIONS[0],
        );
      } catch (err) {
        console.error("Erro ao carregar contatos", err);
      }
    };
    loadContacts();
  }, [user?.uid]);

  if (!userData) return <div>Carregando...</div>;

  return (
    <div className="profile-page container">
      <div className="profile-edit-header">
        <div className="profile-info-section">
          <div className="profile-edit-steps">
            <div className="steps-header">
              {[1, 2, 3, 4].map((s) => (
                <button
                  key={s}
                  className={`step-tab${step === s ? " active" : ""}`}
                  type="button"
                  onClick={() => setStep(s)}
                >
                  {s === 1 && "Informações Pessoais"}
                  {s === 2 && "Aparência"}
                  {s === 3 && "Estilo de Vida & Carreira"}
                  {s === 4 && "Contatos"}
                </button>
              ))}
            </div>
            <form onSubmit={handleSubmit} className="profile-edit-form">
              {step === 1 && (
                <div className="profile-step-content">
                  <div className="edit-form-group">
                    <label>Apelido</label>
                    <input
                      type="text"
                      name="username"
                      value={formData.username || ""}
                      onChange={handleChange}
                      placeholder="Seu nome visível"
                      disabled
                    />
                  </div>

                  <div className="edit-form-group">
                    <label>Data de nascimento</label>
                    <div className="date-group">
                      <select
                        name="birthDay"
                        value={formData.birthDay || ""}
                        onChange={handleChange}
                      >
                        <option value="">Dia</option>
                        {days.map((d) => (
                          <option key={d} value={String(d)}>
                            {d}
                          </option>
                        ))}
                      </select>

                      <select
                        name="birthMonth"
                        value={formData.birthMonth || ""}
                        onChange={handleChange}
                      >
                        <option value="">Mês</option>
                        {months.map((m, index) => (
                          <option key={m} value={String(index + 1)}>
                            {m}
                          </option>
                        ))}
                      </select>

                      <select
                        name="birthYear"
                        value={formData.birthYear || ""}
                        onChange={handleChange}
                      >
                        <option value="">Ano</option>
                        {years.map((y) => (
                          <option key={y} value={String(y)}>
                            {y}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="edit-form-group">
                    <label>País</label>
                    <select
                      name="country"
                      value={formData.country || ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={
                        touched.country && errors.country ? "error" : ""
                      }
                    >
                      <option value="">Selecione seu país</option>

                      <option key="brasil" value="Brasil">
                        Brasil
                      </option>
                    </select>
                    {touched.country && errors.country && (
                      <span className="error-message">{errors.country}</span>
                    )}
                  </div>

                  <div className="edit-form-group">
                    <label>Estado</label>
                    <select
                      name="state"
                      value={formData.state || ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={touched.state && errors.state ? "error" : ""}
                    >
                      <option value="">Selecione seu estado</option>
                      {optionsBrazilianStates.map((uf) => (
                        <option key={uf.value} value={uf.value}>
                          {uf.label}
                        </option>
                      ))}
                    </select>
                    {touched.state && errors.state && (
                      <span className="error-message">{errors.state}</span>
                    )}
                  </div>

                  <div className="edit-form-group">
                    <label>Cidade</label>
                    <select
                      name="city"
                      value={formData.city || ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={touched.city && errors.city ? "error" : ""}
                      disabled={!formData.state}
                    >
                      <option value="">
                        {formData.state
                          ? "Selecione sua cidade"
                          : "Selecione primeiro o estado"}
                      </option>

                      {cities.map((city) => (
                        <option key={city.id} value={city.nome}>
                          {city.nome}
                        </option>
                      ))}
                    </select>
                    {touched.city && errors.city && (
                      <span className="error-message">{errors.city}</span>
                    )}
                  </div>

                  <div className="edit-form-group">
                    <label>Frase de efeito</label>
                    <input
                      type="text"
                      name="bioTitle"
                      value={formData.bioTitle || ""}
                      onChange={handleChange}
                      placeholder="Ex: Procuro alguém especial..."
                    />
                  </div>

                  <div className="edit-form-group">
                    <label>Sobre Mim</label>
                    <textarea
                      name="bioDescription"
                      value={formData.bioDescription || ""}
                      onChange={handleChange}
                      rows={3}
                      placeholder="Conte um pouco sobre você..."
                    />
                  </div>
                </div>
              )}
              {step === 2 && (
                <div className="profile-step-content">
                  <div className="edit-form-group">
                    <label>Tipo de corpo</label>
                    <select
                      name="bodyType"
                      value={formData.bodyType || ""}
                      onChange={handleChange}
                    >
                      <option value="">Selecione</option>
                      {optionsBodyTypes.map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="edit-form-group">
                    <label>Tom de pele</label>
                    <select
                      name="skinTone"
                      value={formData.skinTone || ""}
                      onChange={handleChange}
                    >
                      <option value="">Selecione</option>
                      {optionsSkinTones.map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="edit-form-group">
                    <label>Cor do cabelo</label>
                    <select
                      name="hairColor"
                      value={formData.hairColor || ""}
                      onChange={handleChange}
                    >
                      <option value="">Selecione</option>
                      {optionsHairColors.map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="edit-form-group">
                    <label>Cor dos olhos</label>
                    <select
                      name="eyeColor"
                      value={formData.eyeColor || ""}
                      onChange={handleChange}
                    >
                      <option value="">Selecione</option>
                      {optionsEyeColors.map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="edit-form-group">
                    <label>Altura</label>
                    <select
                      name="height"
                      value={formData.height || ""}
                      onChange={handleChange}
                    >
                      <option value="">Selecione</option>
                      {optionsHeights.map((v) => (
                        <option key={v} value={v}>
                          {v} cm
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              {step === 3 && (
                <div className="profile-step-content">
                  <div className="edit-form-group">
                    <label>Fuma?</label>
                    <select
                      name="smoking"
                      value={formData.smoking || ""}
                      onChange={handleChange}
                    >
                      <option value="">Selecione</option>
                      {optionsSmoking.map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="edit-form-group">
                    <label>Bebe?</label>
                    <select
                      name="drinking"
                      value={formData.drinking || ""}
                      onChange={handleChange}
                    >
                      <option value="">Selecione</option>
                      {optionsDrinking.map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="edit-form-group">
                    <label>Estado civil</label>
                    <select
                      name="maritalStatus"
                      value={formData.maritalStatus || ""}
                      onChange={handleChange}
                    >
                      <option value="">Selecione</option>
                      {optionsMaritalStatus.map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="edit-form-group">
                    <label>Filhos</label>
                    <select
                      name="children"
                      value={formData.children || ""}
                      onChange={handleChange}
                    >
                      <option value="">Selecione</option>
                      {optionsChildren.map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="edit-form-group">
                    <label>Escolaridade</label>
                    <select
                      name="education"
                      value={formData.education || ""}
                      onChange={handleChange}
                    >
                      <option value="">Selecione</option>
                      {optionsEducation.map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="edit-form-group">
                    <label>Profissão</label>
                    <select
                      name="profession"
                      value={formData.profession || ""}
                      onChange={handleChange}
                    >
                      <option value="">Selecione</option>
                      {optionsProfessions.map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              {step === 4 && (
                <div className="profile-step-content">
                  <div className="edit-form-group">
                    <label>Contatos</label>

                    <div className="contact-row">
                      <select
                        value={selectedContactType}
                        onChange={(e) => {
                          const nextType = e.target.value as ContactName;
                          setSelectedContactType(nextType);
                          if (nextType === "WhatsApp") {
                            setContactValues((prev) => ({
                              ...prev,
                              WhatsApp: prev.WhatsApp?.length
                                ? formatWhatsAppBR(prev.WhatsApp)
                                : "+55 ",
                            }));
                          }
                        }}
                      >
                        {CONTACT_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>

                      <input
                        type="text"
                        value={contactValues[selectedContactType] || ""}
                        onChange={(e) => {
                          const raw = e.target.value;
                          setContactValues((prev) => ({
                            ...prev,
                            [selectedContactType]:
                              selectedContactType === "WhatsApp"
                                ? formatWhatsAppBR(raw || "+55 ")
                                : raw,
                          }));
                        }}
                        placeholder={
                          selectedContactType === "WhatsApp"
                            ? "Ex: +55 (11) 99999-9999"
                            : selectedContactType === "Telegram"
                              ? "Ex: @seuusuario"
                              : selectedContactType === "Instagram"
                                ? "Ex: @seuinstagram"
                                : "Digite o contato"
                        }
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="edit-actions-footer">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? "Salvando..." : "Salvar Alterações"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileEditScreen;
